import {
  composeRenderProps,
  Link as AriaLink,
  LinkProps as AriaLinkProps,
} from 'react-aria-components';
import { tv } from 'tailwind-variants';
import { focusRing } from 'src/utils';
import DirLeftIcon from 'src/assets/icons/direction-left.svg';
import DirRightIcon from 'src/assets/icons/direction-right.svg';

import type { ColorVariant } from 'src/types/common';

export const linkButton = tv({
  extend: focusRing,
  base: 'inline-flex flex-shrink-0 items-center gap-2 text-base font-semibold decoration-2 underline-offset-4 transition cursor-pointer hover:underline',
  variants: {
    variant: {
      surface: 'text-surface-alt-ghost-75',
      primary: 'text-primary-ghost-75',
      secondary: 'text-secondary-ghost-75',
      success: 'text-green-ghost-alt-75',
      danger: 'text-red-ghost-alt-75',
      warning: 'text-yellow-ghost-alt-75',
      ai: 'text-ai-ghost-alt-75',
    },
    reversed: {
      true: 'flex-row-reverse',
    },
  },
  defaultVariants: {
    variant: 'surface',
  },
});

export const linkIcon = tv({
  base: 'text-xl',
  variants: {
    variant: {
      surface: 'text-surface-alt-ghost-45',
      primary: 'text-primary-ghost-45',
      secondary: 'text-secondary-ghost-45',
      success: 'text-green-ghost-alt-45',
      danger: 'text-red-ghost-alt-45',
      warning: 'text-yellow-ghost-alt-45',
      ai: 'text-ai-ghost-alt-45',
    },
  },
  defaultVariants: {
    variant: 'surface',
  },
});

export interface LinkButtonProps extends AriaLinkProps {
  variant?: ColorVariant;
  reversed?: boolean;
  hasArrow?: boolean;
}

const LinkButton = ({
  variant = 'surface',
  reversed = false,
  hasArrow = true,
  className,
  children,
  ...props
}: LinkButtonProps) => {
  const DirectionIcon = reversed ? DirLeftIcon : DirRightIcon;

  return (
    <AriaLink
      {...props}
      className={composeRenderProps(className, (cn, renderProps) =>
        linkButton({ ...renderProps, className: cn, variant, reversed })
      )}
    >
      <>
        {children}
        {hasArrow && (
          <DirectionIcon aria-hidden="true" className={linkIcon({ variant })} />
        )}
      </>
    </AriaLink>
  );
};

export default LinkButton;
